import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { safeGetItem } from '~/utils/safeStorage';

const LOCAL_STORAGE_KEY = 'preferredTimezone';

const getBrowserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

// Intl.DateTimeFormat throws RangeError for unrecognised IANA zone strings.
// Use this before trusting any timezone value from localStorage or external input.
export const isValidTimezone = (tz: string): boolean => {
  try { new Intl.DateTimeFormat([], { timeZone: tz }); return true; }
  catch { return false; }
};

interface TimezoneState {
  selectedTimezone: string | null;
  effectiveTimezone: string;
  setTimezone: (tz: string | null) => void;
  clearPreferredTimezone: () => void;
}

export const TimezoneContext = createContext<TimezoneState | null>(null);

interface TimezoneProviderProps {
  children: ReactNode;
}

const TimezoneProvider = ({ children }: TimezoneProviderProps) => {
  const [selectedTimezone, setSelectedTimezone] = useState<string | null>(() => {
    const stored = safeGetItem(LOCAL_STORAGE_KEY);
    if (!stored) return null;
    if (!isValidTimezone(stored)) {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      return null;
    }
    return stored;
  });

  const effectiveTimezone = selectedTimezone && isValidTimezone(selectedTimezone)
    ? selectedTimezone
    : getBrowserTimezone();

  const setTimezone = useCallback((tz: string | null) => {
    if (tz) {
      localStorage.setItem(LOCAL_STORAGE_KEY, tz);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
    setSelectedTimezone(tz);
  }, []);

  const clearPreferredTimezone = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    setSelectedTimezone(null);
  }, []);

  const contextValue = useMemo(
    () => ({ selectedTimezone, effectiveTimezone, setTimezone, clearPreferredTimezone }),
    [selectedTimezone, effectiveTimezone, setTimezone, clearPreferredTimezone],
  );

  return (
    <TimezoneContext.Provider value={contextValue}>
      {children}
    </TimezoneContext.Provider>
  );
};

export default TimezoneProvider;

// Fallback used when useTimezone() is called outside a TimezoneProvider.
// In production this never triggers — TimezoneProvider is mounted in providers.tsx.
// Without this fallback every consumer (FormattedDateTime, MessageBubble, date-picker, etc.)
// would throw in tests that don't explicitly wrap with TimezoneProvider, requiring either
// a provider in renderWithProviders (which breaks tests that partially mock timezoneContext)
// or individual fixes in each consumer.
const fallbackTimezone: TimezoneState = {
  selectedTimezone: null,
  effectiveTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  setTimezone: () => {},
  clearPreferredTimezone: () => {},
};

export const useTimezone = (): TimezoneState => {
  return useContext(TimezoneContext) ?? fallbackTimezone;
};
